import { DataTable } from "@/components/data-table";
import { Spinner } from "@heroui/spinner";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import Footer from "../Footer/Footer";
import { useTable } from "./useTable";

export type ActionItem<TData = unknown> = {
  label?: string;
  route?: string;
  onClick?: (data: TData[]) => void;
  children?: React.ReactNode;
  color?:
    | "default"
    | "primary"
    | "secondary"
    | "success"
    | "warning"
    | "danger"; // default: primary
  variant?:
    | "light"
    | "solid"
    | "bordered"
    | "flat"
    | "faded"
    | "shadow"
    | "ghost"; // default: flat
};

type TableProps<TData, TValue> = {
  label: string;
  table_name: string;
  columns: ColumnDef<TData, TValue>[];
  client: string;
  columnVisibilityInit?: VisibilityState;
  filter?: React.ReactNode;
  new_item?: React.ReactNode;
  audit_id?: number | string;
  sub_entity?: string;
  audit_table_name?: string;
  download_button?: boolean;
  actions?: ActionItem<TData>[];
  enableRowSelection?: boolean;
  selectionActions?: (
    selectedRows: TData[],
    clearSelection: () => void,
  ) => React.ReactNode;
  disable_translations_request?: boolean;
};

export default function Table<TData, TValue>({
  label,
  table_name,
  columns,
  columnVisibilityInit,
  client,
  filter = null,
  new_item = null,
  sub_entity = undefined,
  audit_id = undefined,
  audit_table_name = undefined,
  download_button = true,
  actions = [],
  enableRowSelection = false,
  selectionActions,
  disable_translations_request = false,
}: TableProps<TData, TValue>) {
  const { data, isLoading, error, currentPage, pagesize, setCurrentPage } =
    useTable<TData>({
      client,
      sub_entity,
      table_name,
      audit_table_name,
      audit_id,
      disable_translations_request,
    });

  if (isLoading)
    return (
      <div className="grid place-items-center h-full">
        <Spinner />
      </div>
    );

  if (error) return "An error has occurred: " + error;

  const isArrayData = Array.isArray(data);
  const tableData = isArrayData ? data : data?.data || [];
  const rowCount = isArrayData ? 0 : data?.count || 0;
  const pageCount = isArrayData
    ? 0
    : Math.ceil((data?.count || 0) / Number(pagesize));

  return (
    <section className="py-4 px-8 font-sans font-light leading-5 text-blue-950">
      <DataTable<TData, TValue>
        client={client}
        table_name={table_name}
        label={label}
        columns={columns}
        filter={filter}
        new_item={new_item}
        data={tableData}
        rowCount={rowCount}
        pageCount={pageCount}
        currentPage={Number(currentPage)}
        setCurrentPage={setCurrentPage}
        columnVisibilityInit={columnVisibilityInit}
        download_button={download_button}
        actions={actions}
        enableRowSelection={enableRowSelection}
        selectionActions={selectionActions}
      />
      <Footer />
    </section>
  );
}
